/**
 * Keycloak SSO adapter for the Review UI.
 *
 * DESIGN: the SSO gate lives on the API (`/api/v1/auth/sso-config`), NOT in the
 * UI build. Cutover and kill-switch are an env change + container recreate on
 * at-2 — no image rebuild. The UI asks the API "is SSO on, and with which
 * realm/client?" and only then dynamically imports keycloak-js, so the legacy
 * login page keeps working untouched when SSO is off.
 *
 * keycloak-js init uses `check-sso`: no login redirect on load. An anonymous
 * visitor simply stays anonymous and sees the Sign in button; tokens are
 * requested when the user clicks it. Every API call carries the bearer token
 * (see api.js).
 */

const SSO_CONFIG_URL = "/api/v1/auth/sso-config";

let keycloak = null; // Keycloak instance once initialised
let initPromise = null; // shared init across callers
let ssoEnabled = false; // the API says SSO is on (even if init misbehaves)

/** True when SSO has been initialised and the user holds a valid token. */
export function isSsoAuthenticated() {
  return Boolean(keycloak && keycloak.authenticated);
}

/** Display identity for the header; empty when not signed in. */
export function getSsoUserLabel() {
  if (!keycloak || !keycloak.authenticated) return "";
  const p = keycloak.idTokenParsed || {};
  return p.name || p.preferred_username || p.email || "";
}

/**
 * Returns the Keycloak instance when SSO is enabled AND the session is
 * authenticated; null otherwise (caller falls back to the legacy login page).
 * Safe to call repeatedly — init runs once.
 */
export async function tryInitSso() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    let cfg;
    try {
      const res = await fetch(SSO_CONFIG_URL, { headers: { accept: "application/json" } });
      if (!res.ok) return null;
      cfg = await res.json();
    } catch {
      return null; // API unreachable — behave exactly like SSO-off
    }
    if (!cfg || !cfg.enabled || !cfg.url || !cfg.realm || !cfg.client_id) return null;

    let KeycloakCtor;
    try {
      ({ default: KeycloakCtor } = await import("keycloak-js"));
    } catch {
      return null; // dynamic chunk failed to load — stay on legacy login
    }
    keycloak = new KeycloakCtor({
      url: cfg.url,
      realm: cfg.realm,
      clientId: cfg.client_id,
    });

    ssoEnabled = true;
    try {
      // Plain check-sso WITHOUT silentCheckSsoRedirectUri: Keycloak's default
      // CSP is `frame-ancestors 'self'`, which blocks the silent iframe from
      // the app origin and hangs init forever (seen live 2026-09-30). The
      // non-silent path does a fast top-level prompt=none redirect dance —
      // a couple of 302s and back, no third-party-cookie or CSP dependency.
      // Race a timeout so a wedged init can never strand the login page on
      // "Checking sign-in options…" — worst case the SSO button shows and a
      // click retries via keycloak.login().
      await Promise.race([
        keycloak.init({
          onLoad: "check-sso",
          checkLoginIframe: false,
          pkceMethod: "S256",
        }),
        new Promise((resolve) => setTimeout(resolve, 10_000)),
      ]);
    } catch {
      // Init failed but config said enabled — keep ssoEnabled true so the
      // button still renders and a click can retry.
      return null;
    }
    return keycloak.authenticated ? keycloak : null;
  })();
  return initPromise;
}

/**
 * Initialises SSO once and reports the resulting state:
 *   { enabled: false }                    — API says SSO is off (or unreachable)
 *   { enabled: true, authenticated: false } — SSO on, visitor has no session
 *   { enabled: true, authenticated: true }  — SSO on, valid session
 */
export async function getSsoStatus() {
  await tryInitSso();
  return {
    enabled: ssoEnabled || Boolean(keycloak),
    authenticated: Boolean(keycloak && keycloak.authenticated),
  };
}

/**
 * Ensure a fresh access token before an API call. Returns the token, or null
 * when SSO is off / not authenticated / the refresh failed (caller then lets
 * the request go unauthenticated, which the API will 401).
 */
export async function getSsoAccessToken() {
  if (!keycloak || !keycloak.authenticated) return null;
  try {
    await keycloak.updateToken(30); // refresh if expiring within 30s
    return keycloak.token;
  } catch {
    // Refresh failed — session is effectively over.
    keycloak.clearToken();
    return null;
  }
}

/** Redirect to Keycloak's login page (interactive sign-in). */
export async function ssoLogin() {
  if (!keycloak) await tryInitSso();
  if (keycloak) await keycloak.login({ redirectUri: window.location.origin + "/" });
}

/** Logout via Keycloak's end_session endpoint, then land on the login page. */
export function ssoLogout() {
  if (keycloak) {
    keycloak.logout({ redirectUri: window.location.origin + "/" });
  } else {
    window.location.reload();
  }
}
